import Cookies from 'js-cookie';
import qs from 'qs';
import Router from '@/router';
import validator from '@/utils/validator';
import ProjectRoutesMap from '@/router/project';
import {
  RMB_UNIT,
  REDIRECT_NAME,
  PER_MINUTE,
  PER_HOUR,
  PER_DAY,
  RECORD_AUTH_NAME,
  BASE_URL,
  HOME_URL,
  UID_NAME,
  ALERT_MESSAGE_NAME,
  TOKEN_NAME,
  EVENT_LOG_CONFIG_NAME,
  APP_ROUTE_MAP,
  AUTOPLAY_NEXT_SETTING_NAME,
  TURN_OFF_TIME_SETTING_NAME,
  PROMPT_MESSAGE_NAME,
  DISCOUNT_IDENTIFICATION,
  COUPON_IDENTIFICATION,
  PLAYED_TIME_PREFIX,
  PLAYED_TIME_RANGE_MAX,
  MINI_USER_AGENT_SYMBOL,
  IPHONE_USER_AGENT_SYMBOL,
  ANDROID_USER_AGENT_SYMBOL,
  SEARCH_HISTORY_NAME,
  SEARCH_HISTORY_SPLIT_SYMBOL,
  PLAYER_NEWEST_PLAYLIST,
  PLAYER_PLAY_LIST_SORT,
  PLAYER_LAST_PROGRAM_ID,
  PLAYER_SPEED_PLAY,
  DOWNLOAD_BAR_HEIGHT_KEY,
  SECKILL_GUIDE_MASK_NAME,
  PAID_REFRESH_DETAIL,
} from '@/utils/constant';

/**
 * 驼峰转下划线
 * @param str
 * @returns {string}
 */
export function humpToUnderline(str) {
  return str.replace(/([A-Z])/g, '-$1').toLowerCase();
}

/**
 * 下划线转驼峰
 * @param str
 */
export function underlineToHump(str) {
  return str.replace(/-(\w)/g, (all, letter) => letter.toUpperCase());
}

/**
 * 数字转中文
 */
export function numberToCh(num) {
  const arr1 = ['零', '一', '二', '三', '四', '五', '六', '七', '八', '九'];
  const arr2 = ['', '十', '百', '千', '万', '十', '百', '千', '亿', '十', '百', '千', '万', '十', '百', '千', '亿'];
  if (!num || Number.isNaN(num)) {
    return '零';
  }
  const english = num.toString().split('');
  let result = '';
  for (let i = 0; i < english.length; i++) {
    const desIndex = english.length - 1 - i;
    result = arr2[i] + result;
    const arr1Index = english[desIndex];
    result = arr1[arr1Index] + result;
  }
  result = result.replace(/零(千 | 百 | 十)/g, '零').replace(/十零/g, '十');
  result = result.replace(/零+/g, '零');
  result = result.replace(/零亿/g, '亿').replace(/零万/g, '万');
  result = result.replace(/亿万/g, '亿');
  result = result.replace(/零+$/, '');
  result = result.replace(/^一十/g, '十');
  return result;
}

/**
 * 首字母大写
 * @param word
 */
export function upperCaseFirst(word) {
  return word.replace(/^\S/, letter => letter.toUpperCase());
}

/**
 * 格式化手机，显示星号
 * @param mobile
 */
export function formatMobile(mobile) {
  return mobile.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2');
}

/**
 * 获取JS 日期对象
 * @param source
 * @returns {*}
 */
export function getJsDateObject(source) {
  let time = source;
  let date;
  if (typeof time === 'object') {
    date = time;
  } else {
    if ((`${time}`).length === 10) time = parseInt(time, 10) * 1000;
    date = new Date(time);
  }
  return date;
}

/**
 * 左部填充
 * @param string
 * @param ch
 * @param len
 * @returns {string}
 */
export function leftPad(string, ch = '', len = 2) {
  let str = `${string}`;
  const diff = len - str.length;
  if (diff > 0) {
    str = new Array(diff + 1).join(ch) + str;
  }
  return str;
}

/**
 * 解析时间
 * @param time
 * @param leftPadStr
 * @returns {{days: string, hours: string, minutes: string, seconds: string}}
 */
export function parseTime(time, leftPadStr = '0') {
  const days = leftPad(Math.floor(time / PER_DAY), leftPadStr);
  const hours = leftPad(Math.floor((time % PER_DAY) / PER_HOUR), leftPadStr);
  const minutes = leftPad(Math.floor((time / PER_MINUTE) % 60), leftPadStr);
  const seconds = leftPad(Math.floor((time % PER_MINUTE) / 1000), leftPadStr);
  const mSeconds = Math.floor((time % 1000) / 100);
  return {
    days,
    hours,
    minutes,
    seconds,
    mSeconds,
  };
}

/**
 * 解析时间
 * @param source
 * @param cFormat
 * @param pad
 * @returns {*}
 */
export function formatTime(source, cFormat = 'yyyy-MM-dd HH:ii:ss', pad = true) {
  if (arguments.length === 0) {
    return null;
  }
  const format = cFormat;
  const date = getJsDateObject(source);
  const formatObj = {
    y: date.getFullYear(),
    M: date.getMonth() + 1,
    d: date.getDate(),
    H: date.getHours(),
    i: date.getMinutes(),
    s: date.getSeconds(),
    a: date.getDay(),
  };
  return format.replace(/(y|M|d|H|i|s|a)+/g, (result, key) => {
    let value = formatObj[key];
    if (key === 'a') return ['日', '一', '二', '三', '四', '五', '六'][value];
    if (pad && result.length > 0 && value < 10) {
      value = `0${value}`;
    }
    return value || 0;
  });
}

/**
 * 格式化年份
 * @param list
 * @param timeKey
 * @returns {*}
 */
export function formatYear(list, timeKey) {
  if (list.length === 0) return list;
  let prevItemYear = formatTime(new Date(), 'yyyy');
  list.forEach((item) => {
    const currentItemYear = formatTime(item[timeKey], 'yyyy');
    item.hideYear = prevItemYear === currentItemYear;
    prevItemYear = currentItemYear;
  });
  return list;
}

/**
 * 格式化用户年龄和地区
 * @param age
 * @param area
 * @param separator
 * @returns {*}
 */
export function formatUserAgeAndArea(age, area, separator = ' ') {
  if (age == null || age === '') return area;
  if (area == null || area === '') return age;
  return `${age}${separator}${area}`;
}

export function formatMessageTime(source) {
  if (arguments.length === 0) {
    return null;
  }
  const currentTime = new Date();
  if (formatTime(source, 'yyyy-MM-dd') === formatTime(currentTime, 'yyyy-MM-dd')) {
    return formatTime(source, 'HH:ii');
  }
  if (formatTime(source, 'yyyy') === formatTime(currentTime, 'yyyy')) {
    return formatTime(source, 'MM-dd');
  }
  return formatTime(source, 'yyyy-MM-dd');
}

export function formatPriceRange(prices) {
  const [min, max] = prices;
  let priceRange = min;
  if (min < max) {
    priceRange = prices.join('-');
  }
  return priceRange;
}

/**
 * 格式化价格
 * @param price
 * @param addPriceUnit
 * @returns {string}
 */
export function formatPrice(price, addPriceUnit) {
  if (!addPriceUnit) return `${price}`;
  return `${RMB_UNIT}${price}`;
}

/**
 * 格式化价格（含0元处理）
 * @param price
 * @param addPriceUnit
 * @returns {string}
 */
export function formatPriceWithFree(price, addPriceUnit) {
  if (price === 0) return '免费';
  return formatPrice(price, addPriceUnit);
}

/**
 * 格式化价格成HTML
 * @param number
 * @param formatFree
 * @param addPriceUnit
 * @returns {string}
 */
export function formatPriceHtml(number, formatFree = true, addPriceUnit = true) {
  let price = formatPriceWithFree(number, addPriceUnit);
  if (!formatFree) {
    price = formatPrice(number, addPriceUnit);
  }
  return price.replace(new RegExp(`^${RMB_UNIT}`), (...args) => `<span class="price-unit">${args[0]}</span>`);
}


/**
 * 时长转具体时间
 * @param string
 * @returns {number}
 */
export function durationToTime(string) {
  const duration = string.toString();
  if (duration.search(':') === -1) {
    return parseInt(duration, 10);
  }
  const array = duration.split(':');
  return (parseInt(array[0], 10) * 60) + parseInt(array[1], 10);
}

/**
 * 具体时间转时长
 * @param number
 * @returns {string}
 */
export function timeToDuration(number) {
  if (number == null) {
    return '00:00';
  }
  let time = number;
  if (typeof time === 'string') {
    time = parseInt(number, 10);
  }
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${leftPad(minutes, '0')}:${leftPad(seconds, '0')}`;
}

/**
 * url encode
 * @param url
 * @returns {string}
 */
export function encodeUrl(url) {
  return encodeURIComponent(url.toString());
}

/**
 * url decode
 * @param url
 * @returns {string}
 */
export function decodeUrl(url) {
  return decodeURIComponent(url.toString());
}

/**
 * 解析query
 * @param str
 * @returns {{}}
 */
export function parseQuery(str) {
  const search = str.split('?');
  const query = search.length > 1 ? search[1] : '';
  return qs.parse(query);
}

/**
 * 生成query 参数
 * @param options
 * @returns {string}
 */
export function generateQuery(options) {
  const query = [];
  Object.keys(options).forEach((key) => {
    if (options[key] != null) {
      query.push(`${key}=${encodeUrl(options[key])}`);
    }
  });
  return query.join('&');
}

/**
 * 获取解析后的query
 * @param options
 * @returns {{}}
 */
export function getQuery(options) {
  const obj = {};
  Object.keys(options).forEach((key) => {
    obj[key] = decodeUrl(options[key]);
  });
  return obj;
}

/**
 * 获取BaseUrl
 * @param url
 */
export function getBaseUrl(url) {
  const base = url.split('?');
  return base[0].replace(/\?[(&*)(\w*)(=*)(\w*)]*$/g, '');
}

/**
 * 生成URL
 * @param baseUrl
 * @param options
 * @returns {string}
 */
export function generateUrl(baseUrl, options) {
  return validator.isEmptyObj(options) ? baseUrl : `${baseUrl}?${generateQuery(options)}`;
}

/**
 * 追加query
 * @param str
 * @param options
 * @returns {string}
 */
export function appendQuery(str, options) {
  const base = getBaseUrl(str);
  const parse = parseQuery(str);
  const query = Object.assign({}, parse, options);
  return generateUrl(base, query);
}

/**
 * 数据打点
 * @param url
 * @param options
 */
export function collectData(url, options) {
  const img = document.createElement('img');
  img.src = generateUrl(url, options);
}

/**
 * 监控
 * @param error
 */
export function monitor(error) {
  if (!window.Sentry) return;
  window.Sentry.captureException(error);
}

/**
 * 设置监控用户
 * @param id
 */
export function setMonitorUser(id) {
  if (!window.Sentry) return;
  window.Sentry.configureScope((scope) => {
    scope.setUser({ id });
  });
}

/**
 * 简单加密
 * @param code
 * @returns {string}
 */
export function compile(code) {
  let result = String.fromCharCode(code.charCodeAt(0) + code.length);
  for (let i = 1; i < code.length; i++) {
    result += String.fromCharCode(code.charCodeAt(i) + code.charCodeAt(i - 1));
  }
  return encodeURI(result);
}

/**
 * 是否支持本地缓存storage
 * @returns {boolean}
 */
export function supperStorage() {
  try {
    window.localStorage.setItem('test', '');
    window.localStorage.removeItem('test');
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * 从本地缓存获取值
 * @param name
 * @returns {string | null}
 */
export function getFromLocalStorage(name) {
  return window.localStorage.getItem(name);
}

/**
 * 设置到本地缓存
 * @param name
 * @param value
 */
export function setToLocalStorage(name, value) {
  return window.localStorage.setItem(name, value);
}

/**
 * 从本地缓存删除
 * @param name
 */
export function removeFromLocalStorage(name) {
  return window.localStorage.removeItem(name);
}

/**
 * 获取根域名
 * @returns {string}
 */
export function getRootDomain() {
  const KEY = `__TEST_RD__${Date.now()}`;
  const Reg = new RegExp(`(^|;)\\s*${KEY}=1`);
  const domain = document.domain || window.location.hostname;
  const list = domain.split('.');
  let len = list.length;
  let tempDomain = '';
  let tempCookie = '';

  // eslint-disable-next-line no-plusplus
  while (len--) {
    tempDomain = list.slice(len).join('.');
    tempCookie = `${KEY}=1;domain=.${tempDomain}`;

    document.cookie = tempCookie;

    if (Reg.test(document.cookie)) {
      document.cookie = `${tempCookie};expires=${(new Date(0)).toUTCString()}`;
      return tempDomain;
    }
  }
  return '';
}

/**
 * 获取cookie 配置
 * @param path
 * @returns {{path: string}}
 */
export function getCookieOptions(path = '/') {
  const options = {
    path,
  };
  const domain = getRootDomain();
  if (domain !== '') {
    options.domain = domain;
  }
  return options;
}

/**
 * 从缓存中获取值
 * @param name
 * @returns {*}
 */
export function getFromStorage(name) {
  if (!supperStorage()) {
    return Cookies.get(`${name}`) || '';
  }
  return getFromLocalStorage(name);
}

/**
 * 设置缓存
 * @param name
 * @param value
 * @returns {*}
 */
export function setToStorage(name, value) {
  if (!supperStorage()) {
    return Cookies.set(`${name}`, value, { path: '/' });
  }
  return setToLocalStorage(name, value);
}

/**
 * 删除缓存
 * @param name
 * @returns {*}
 */
export function removeFormStorage(name) {
  if (!supperStorage()) {
    return Cookies.remove(`${name}`, { path: '/' });
  }
  return removeFromLocalStorage(name);
}

/**
 * 获取UID
 */
export function getUID() {
  return Cookies.get(`${UID_NAME}`) || 0;
}

/**
 * 缓存验证
 * @param val
 * @returns {*}
 */
export function setAuth(val) {
  return setToStorage(`${TOKEN_NAME}`, val);
}

/**
 * 获取token
 */
export function getAuth() {
  return getFromStorage(`${TOKEN_NAME}`) || '';
}

/**
 * 设置WebView 中的token 至cookie
 * @param val
 * @returns {*}
 */
export function setWebViewAuth(val) {
  return Cookies.set(`${TOKEN_NAME}`, val);
}

/**
 * 获取微信录音权限
 * @returns {*}
 */
export function getRecordAuth() {
  return getFromStorage(RECORD_AUTH_NAME);
}

/**
 * 设置微信录音权限
 * @returns {*}
 */
export function setRecordAuth() {
  return setToStorage(RECORD_AUTH_NAME, 'true');
}

/**
 * 设置App配置
 * @param config
 * @returns {*}
 */
export function setEventLogConfig(config) {
  const value = qs.stringify(config);
  return setToStorage(EVENT_LOG_CONFIG_NAME, value);
}

/**
 * 获取App配置
 * @returns {{}}
 */
export function getEventLogConfig() {
  const result = getFromStorage(EVENT_LOG_CONFIG_NAME);
  if (result == null) {
    return {};
  }
  return qs.parse(result);
}

/**
 * 设置App 路由映射
 * @param map
 * @returns {*}
 */
export function setAppRouteMap(map) {
  const value = JSON.stringify(map);
  return setToStorage(APP_ROUTE_MAP, value);
}

/**
 * 获取App 路由映射
 * @returns {{}}
 */
export function getAppRouteMap() {
  const result = getFromStorage(APP_ROUTE_MAP);
  if (!result) {
    return [];
  }
  return JSON.parse(result);
}

/**
 * 设置自动播放控制
 * @param value
 * @returns {*}
 */
export function setAutoPlayNextControl(value) {
  return setToStorage(AUTOPLAY_NEXT_SETTING_NAME, value);
}

/**
 * 获取自动播放控制
 * @returns {*}
 */
export function getAutoPlayNextControl() {
  return getFromStorage(AUTOPLAY_NEXT_SETTING_NAME) || 'all';
}

/**
 * 设置定时关闭控制
 * @param setting
 * @returns {*}
 */
export function setTurnOffTimeControl(setting) {
  const value = qs.stringify(setting);
  return setToStorage(TURN_OFF_TIME_SETTING_NAME, value);
}

/**
 * 获取定时关闭控制
 * @returns {*|Object}
 */
export function getTurnOffTimeControl() {
  const result = getFromStorage(TURN_OFF_TIME_SETTING_NAME);
  if (!result) {
    return {
      type: 'off',
      key: 'off',
      value: 'off',
    };
  }
  return qs.parse(result);
}

/**
 * 缓存搜索历史
 * @param history
 * @returns {*}
 */
export function setSearchHistory(history) {
  const value = history.join(SEARCH_HISTORY_SPLIT_SYMBOL);
  return setToStorage(SEARCH_HISTORY_NAME, value);
}

/**
 * 获取搜索历史
 */
export function getSearchHistory() {
  const history = getFromLocalStorage(SEARCH_HISTORY_NAME);
  if (history == null || history === '') {
    return [];
  }
  return history.split(SEARCH_HISTORY_SPLIT_SYMBOL);
}

/**
 * 获取弹窗信息
 * @returns {*}
 */
export function getAlertMessage() {
  return Cookies.get(`${ALERT_MESSAGE_NAME}`);
}

/**
 * 删除弹窗信息
 * @returns {*|void}
 */
export function removeAlertMessage() {
  return Cookies.remove(`${ALERT_MESSAGE_NAME}`, getCookieOptions(''));
}

/**
 * 缓存提示信息
 * @param value
 * @returns {*}
 */
export function setPromptMessage(value) {
  return setToStorage(PROMPT_MESSAGE_NAME, qs.stringify(value));
}

/**
 * 获取提示信息
 * @returns {*}
 */
export function getPromptMessage() {
  return qs.parse(getFromStorage(PROMPT_MESSAGE_NAME));
}

/**
 * 是否微信浏览器内
 * @returns {boolean}
 */
export function isWeiXinBrowser() {
  return (/micromessenger/i).test(window.navigator.userAgent);
}

/**
 * 获取用户UserAgent
 * @returns {string}
 */
export function getUserAgent() {
  return window.navigator.userAgent;
}

/**
 * 判断是否在Mini webView
 * @returns {boolean}
 */
export function isMiniWebView() {
  const ua = getUserAgent();
  const reg = new RegExp(MINI_USER_AGENT_SYMBOL, 'i');
  return ua.search(reg) > -1;
}

/**
 * 判断是否在App webView
 * @returns {boolean}
 */
export function isAppWebView() {
  const ua = getUserAgent();
  const iPhone = ua.indexOf(IPHONE_USER_AGENT_SYMBOL) > -1;
  const androidPhone = ua.indexOf(ANDROID_USER_AGENT_SYMBOL) > -1;
  return iPhone || androidPhone;
}

/**
 * 判断是否在iOS App webView
 * @returns {boolean}
 */
export function isIosWebView() {
  return getUserAgent().indexOf(IPHONE_USER_AGENT_SYMBOL) > -1;
}

/**
 * 判断是否在PC
 * @returns {boolean}
 */
export function isPC() {
  const userAgentInfo = navigator.userAgent;
  const Agents = ['Android', 'iPhone', 'iPad', 'iPod', 'Windows Phone'];
  return !Agents.find(item => userAgentInfo.includes(item));
}

/**
 * 是否在web 环境
 * @returns {boolean}
 */
export function isWeb() {
  return !isAppWebView() && !isMiniWebView();
}

/**
 * 是否移动端web 环境
 * @returns {boolean}
 */
export function isMobileWeb() {
  return isWeb() && !isPC();
}

/**
 * 是否安卓系统
 * @returns {boolean}
 */
export function isAndroid() {
  const ua = getUserAgent();
  return ua.indexOf('Android') > -1 || ua.indexOf('Adr') > -1;
}

/**
 * 是否iOs系统
 * @returns {boolean}
 */
export function isIOS() {
  const ua = getUserAgent();
  return !!ua.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/);
}

/**
 * 是否iPad系统
 * @returns {boolean}
 */
export function isIPad() {
  const ua = getUserAgent();
  return ua.indexOf('iPad') > -1;
}

/**
 * 获取当前页面query
 * @returns {{}}
 */
export function getLocationQuery() {
  const { location } = window;
  const { search } = location;
  let query = {};
  if (search !== '') {
    query = parseQuery(search);
  }
  return query;
}

/**
 * 获取跳转的URL
 * @returns {string}
 */
export function getRedirectPath() {
  const { location } = window;
  const { pathname } = location;
  const blackList = [
    `${BASE_URL}/login`,
    `${BASE_URL}/login/mobile`,
    `${BASE_URL}/register`,
    `${BASE_URL}/password/reset`,
    `${BASE_URL}/settings/mobile`,
  ];
  const query = getLocationQuery();
  const { redirectTo } = query;
  if (redirectTo) return redirectTo;
  if (!blackList.includes(pathname)) {
    return pathname;
  }
  return `${HOME_URL}`;
}

/**
 * 缓存回退url
 */
export function storageRedirectPath() {
  const path = getRedirectPath();
  // 缓存回退url
  setToStorage(REDIRECT_NAME, path);
}

/**
 * 获取缓存的回退url
 * @returns {*}
 */
export function getStorageRedirect(redirectTo) {
  if (redirectTo && redirectTo !== '') {
    return redirectTo;
  }
  const result = getFromStorage(REDIRECT_NAME);
  if (result != null && result !== '') {
    return result;
  }
  return HOME_URL;
}

/**
 * 删除回退url
 */
export function removeStorageRedirect() {
  removeFormStorage(REDIRECT_NAME);
}

/**
 * 外链跳转
 * @param str
 * @param type
 */
export function locationHref(str, type = 'push') {
  if (!['replace', 'push'].includes(type)) {
    new Error(`redirectToLoginPage type error: ${type}`);
  }
  storageRedirectPath();
  if (type === 'replace') {
    window.location.replace(str);
    return;
  }
  window.location.href = `${str}`;
}

/**
 * 路径跳转
 * @param path 域名后的路径
 * @param stay * -> 需要后退；1 -> 不需要后退
 */
export function locationPath(path = '', stay) {
  storageRedirectPath();
  const href = `${window.location.protocal || 'http:'}//${window.location.host}${path}`;
  if (stay === 1) {
    window.location.replace(href);
    return;
  }
  window.location.href = `${href}`;
}

/**
 * 跨项目路由跳转
 * @param route 同router的命名路由对象结构一致，兼容不用跨项目则为本项目跳转
 * @param stay * -> 需要后退；1 -> 不需要后退
 */
export function projectJump(route, stay) {
  if (!route || !route.name) {
    console.error('projectJump receive named routing objects');
    return;
  }
  const targetRoute = ProjectRoutesMap.find(item => item.name === route.name);
  if (!targetRoute) {
    Router[stay === 1 ? 'replace' : 'push'](route);
    return;
  }
  if (route.query) {
    locationPath(`${targetRoute.path}?${generateQuery(route.query)}`, stay);
    return;
  }
  if (route.params) {
    Object.keys(route.params).forEach((key) => {
      const reg = typeof route.params[key] === 'number' ? `:${key}(\\d+)` : `:${key}(\\w+)`;
      targetRoute.path = targetRoute.path.replace(reg, route.params[key]);
    });
    locationPath(targetRoute.path, stay);
    return;
  }
  locationPath(targetRoute.path, stay);
}

/**
 * 打开App页面
 * @param url
 * @param stay 0 -> App页面路由需要后退； 1 -> App页面路由不需要后退；
 */
export function handleRedirectAPPPage(url, stay = 0) {
  const canRedirect = window.ymRedirectNative && typeof window.ymRedirectNative === 'function';
  if (canRedirect) {
    window.ymRedirectNative({
      url,
      stay,
    });
  } else {
    locationHref(url);
  }
}

/**
 * 跳转至登录页
 * @param router
 * @param type
 */
export function redirectToLoginPage(router, type = 'replace') {
  if (!['replace', 'push'].includes(type)) {
    new Error(`redirectToLoginPage type error: ${type}`);
  }
  if (isAppWebView()) {
    handleRedirectAPPPage('youthmba://login/login');
    return;
  }
  const { fullPath } = router.history.current;
  const route = { name: 'Login', query: { redirectTo: fullPath } };
  router[type](route);
}

/**
 * 获取产品主页路径
 * @param type
 * @returns {*}
 */
export function getProductIndexPath(type) {
  switch (type) {
    case 'course':
      return { name: 'Course' };
    case 'audio':
      return { name: 'Audio' };
    case 'parents':
      return { name: 'Parents' };
    case 'mine':
      return { name: 'Mine' };
    default:
      return { name: 'Home' };
  }
}

/**
 * 获取产品路径
 * @param type
 * @param id
 * @returns {*}
 */
export function getProductPath(type, id) {
  switch (type) {
    case 'audio':
      return { name: 'AudioDetail', params: { id } };
    case 'program':
      return { name: 'AudioProgramDetail', params: { id } };
    case 'course':
      return { name: 'CourseDetail', params: { id } };
    case 'project':
      return { name: 'ProjectDetail', params: { id } };
    case 'combo':
      return { name: 'ComboDetail', params: { id } };
    case 'audioCombo':
      return { name: 'AudioComboDetail', params: { id } };
    case 'courseCombo':
      return { name: 'CourseComboDetail', params: { id } };
    case 'multiCombo':
      return { name: 'MultiComboDetail', params: { id } };
    case 'groupbuyTeam':
      return { name: 'GroupBuyCreate', params: { id } };
    case 'groupbuyMember':
      return { name: 'GroupBuyTeamDetail', params: { id } };
    case 'reading':
      return { name: 'TrainingDetail', params: { id } };
    case 'vip':
      return { name: 'VipDetail', params: { id } };
    case 'work':
      return { name: 'WorkDetail', params: { id } };
    case 'topic':
      return { name: 'TopicDetail', params: { id } };
    case 'learn':
      return { name: 'PlanDetail', params: { id } };
    case 'entity':
      return { name: 'EntityDetail', params: { id } };
    case 'studentGains':
      return { name: 'StudentGainsDetail', params: { id } };
    case 'intensiveReading':
      return { name: 'MillionReadInterpretBook', query: { id } };
    case 'millionVip':
      return { name: 'MillionReadVipDetail', params: { id } };
    case 'coinProduct':
      return { name: 'ExchangeMallShopDetail', params: { id } };
    default:
      return { name: 'Home' };
  }
}

/**
 * px 转换rem
 * @param px
 * @param viewportWidth
 * @returns {string}
 */
export function addUnit(px, viewportWidth = 750) {
  return `${px / (viewportWidth / 10)}rem`;
}

/**
 * 裁切字符串
 * @param str
 * @param reg
 */
export function sliceString(str, reg) {
  return str.match(new RegExp(reg));
}

/**
 * 裁切优惠活动ID
 * @param uniqueId
 * @returns {*}
 */
export function sliceDiscountId(uniqueId) {
  const result = sliceString(uniqueId, `^((-?)\\d+)${DISCOUNT_IDENTIFICATION}$`);
  if (result) {
    return result[1];
  }
  return '';
}

/**
 * 裁切优惠码ID
 * @param uniqueId
 * @returns {*}
 */
export function sliceCouponId(uniqueId) {
  const result = sliceString(uniqueId, `^(\\d+)${COUPON_IDENTIFICATION}$`);
  if (result) {
    return result[1];
  }
  return '';
}

/**
 * 获取播放器最新一条播放记录的缓存名
 * @returns {string}
 */
export function getPlayerLastProgramIdStorageName() {
  const uid = getUID();
  return `${PLAYER_LAST_PROGRAM_ID}${uid}`;
}

/**
 * 获取播放记录缓存名
 * @returns {string}
 */
export function getPlayTimeStorageName() {
  const uid = getUID();
  return `${PLAYED_TIME_PREFIX}${uid}`;
}

/**
 * 获取播放记录缓存
 * @returns {null}
 */
export function getPlayTimeStorage() {
  const storageName = getPlayTimeStorageName();
  const value = getFromLocalStorage(storageName);
  if (value != null) {
    return qs.parse(value, { arrayLimit: PLAYED_TIME_RANGE_MAX });
  }
  return null;
}

/**
 * 获取对应type 与ID 的某一条播放记录
 * @param id
 * @param type
 * @returns {*}
 */
export function getStoragePlayTimeItem(id, type) {
  const value = getPlayTimeStorage();
  if (value != null) {
    return value[`${type}-${id}`];
  }
  return null;
}

/**
 * 缓存播放记录
 * @param id
 * @param type
 * @param params
 * @param value
 */
export function storagePlayTimeParams(id, type, params, value) {
  if (!supperStorage()) return;
  const storageName = getPlayTimeStorageName();
  let item = getStoragePlayTimeItem(id, type);
  let storage = getPlayTimeStorage();
  if (storage == null) {
    storage = {};
  }
  if (item == null) {
    item = {};
  }
  item[params] = value;
  storage[`${type}-${id}`] = item;
  setToLocalStorage(storageName, qs.stringify(storage));
}

/**
 * 缓存播放时间时长
 * @param id
 * @param type
 * @param duration
 */
export function storagePlayTimeDuration(id, type, duration) {
  storagePlayTimeParams(id, type, 'duration', duration);
}

/**
 * 缓存音视频播放开始时间
 * @param id
 * @param type
 * @param start
 * @returns {*}
 */
export function storageStartPlayTime(id, type, start) {
  storagePlayTimeParams(id, type, 'start', parseFloat(start));
}

/**
 * 获取已缓存音视频播放开始时间
 * @param id
 * @param type
 * @returns {number}
 */
export function getStorageStartPlayTime(id, type) {
  if (!supperStorage()) return 0;
  const item = getStoragePlayTimeItem(id, type);
  if (item == null) return 0;
  const { start } = item;
  if (!start) return 0;
  return parseFloat(start);
}

/**
 * 删除已缓存音视频播放开始时间
 * @param id
 * @param type
 */
export function removeStorageStartPlayTime(id, type) {
  if (!supperStorage()) return;
  storageStartPlayTime(id, type, 0);
}

/**
 * 缓存音视频已播放时间段
 * @param id
 * @param type
 * @param range
 */
export function storagePlayedTimeRange(id, type, range) {
  storagePlayTimeParams(id, type, 'range', range);
}

/**
 * 获取已缓存音视频已播放时间段
 * @param id
 * @param type
 * @returns {*}
 */
export function getStoragePlayedTimeRange(id, type) {
  if (!supperStorage()) return [];
  const item = getStoragePlayTimeItem(id, type);
  if (item == null) return [];
  const { range } = item;
  if (!range) return [];
  return range;
}

/**
 * 删除已缓存音视频已播放时间段
 * @param id
 * @param type
 */
export function removeStoragePlayTimeRange(id, type) {
  storagePlayedTimeRange(id, type, []);
}

/**
 * 设置视频播放器
 * @param $parent
 */
export function setupNativeVideoPlayer($parent) {
  if ($parent == null) return;
  const $videos = Array.from($parent.getElementsByTagName('video'));
  const bodyWidth = `${document.body.offsetWidth}px`;
  const videoHeight = `${(bodyWidth * 9 / 16).toFixed(2)}px`;
  $videos.forEach((item) => {
    // 去除下载
    item.setAttribute('controlslist', 'nodownload');
    // 增加预加载
    item.setAttribute('preload', 'auto');
    // 增加自动播放
    // item.setAttribute('autoplay', 'autoplay');
    // 设置样式
    item.style.width = bodyWidth;
    item.style.height = videoHeight;
  });
}

/**
 * 设置富文本框内置元素
 * @param $parent
 */
export function setupRichTextElement($parent) {
  if ($parent == null) return;
  // 设置视频播放器
  setupNativeVideoPlayer($parent);
  // 设置图片最大宽度
  const $imgs = Array.from($parent.getElementsByTagName('img'));
  $imgs.forEach((item) => {
    item.style.maxWidth = '100%';
  });
  // 设置表格
  const $tables = Array.from($parent.getElementsByTagName('table'));
  $tables.forEach((item) => {
    item.style.width = 'auto';
  });
}

/**
 * 播放器播放列表缓存名称
 * @returns { string }
 */
export function getUserPlaylistStorageName() {
  const uid = getUID();
  return `${PLAYER_NEWEST_PLAYLIST}-${uid}`;
}

/**
 * 播放器播放列表排序缓存名称
 * @returns { string }
 */
export function getUserPlaylistSortStorageName() {
  const uid = getUID();
  return `${PLAYER_PLAY_LIST_SORT}-${uid}`;
}

/**
 * 播放器倍速缓存名称
 * @returns { string }
 */
export function getSpeedPlayStorageName() {
  return `${PLAYER_SPEED_PLAY}-${getUID()}`;
}

/**
 * ViewPort 按DPR=2.0，获取绘画缩放比例
 * @param designSize 设计稿中的待绘制图片宽度
 * @param finalImgSize 最终保存的图片宽度
 * @returns {number} 缩放比例
 */
export function getPosterDrawScale(designSize, finalImgSize = 1080) {
  return (finalImgSize / (designSize / 2)).toFixed(2);
}

/**
 * 判断downloadBar Storage是否存在
 * @param key
 * @returns {boolean}
 */
export function isDownBarHide(key) {
  const downloadStorage = getFromLocalStorage(key);
  return Boolean(downloadStorage);
}


/**
 * 设置下载栏高度
 * @param height
 * @returns {*}
 */
export function setDownloadBarHeight(height) {
  return setToStorage(DOWNLOAD_BAR_HEIGHT_KEY, height);
}

/**
 * 获取下载栏高度
 * @returns {*}
 */
export function getDownloadBarHeight() {
  return Number(getFromStorage(DOWNLOAD_BAR_HEIGHT_KEY));
}

/**
 * 依据顶部栏获取RouterView 高度
 * @returns {string}
 */
export function getRouterViewHeight() {
  const HEADER_HEIGHT = 80;
  const downloadBarHeight = getDownloadBarHeight();
  if (downloadBarHeight > 0) {
    return `calc(100%  - ${addUnit(HEADER_HEIGHT)} - ${addUnit(downloadBarHeight)})`;
  }
  return `calc(100%  - ${addUnit(HEADER_HEIGHT)})`;
}

/**
 * 设置秒杀引导蒙层缓存
 * @returns {*}
 */
export function setSeckillGuideMaskStorage() {
  return setToStorage(SECKILL_GUIDE_MASK_NAME, '1');
}

/**
 * 获取秒杀引导蒙层缓存
 * @returns {*}
 */
export function getSeckillGuideMaskStorage() {
  return getFromStorage(SECKILL_GUIDE_MASK_NAME);
}

/**
 * 获取学习模块引导弹窗的缓存名
 * @returns {string}
 */
export function getLearningModuleGuideStorageName() {
  const uid = getUID();
  return `ym-learning-module-guide-popup-${uid}`;
}

/**
 * 设置已支付需刷新详情
 * @param type
 * @param id
 */
export function setPaidRefreshDetail(type, id) {
  const route = getProductPath(type, id);
  setToStorage(PAID_REFRESH_DETAIL, route);
}

/**
 * 获取已支付需刷新详情
 * @returns {*}
 */
export function getPaidRefreshDetail() {
  return getFromStorage(PAID_REFRESH_DETAIL);
}

/**
 * 设置YmDevice
 * @param device
 */
export function setYmDevice(device) {
  const options = Object.assign({}, getCookieOptions('/'), {
    expires: 3650,
  });
  Cookies.set('ymDevice', device, options);
}

/*
 * 获取YmDevice
 * @returns {*|string}
 */
export function getYmDevice() {
  return Cookies.get('ymDevice') || '';
}

/**
 * 更新安卓app
 */
export function updateAndroidApp() {
  const canUpdate = window.updateApp && typeof window.updateApp === 'function';
  if (canUpdate) {
    window.updateApp();
  }
}

/**
 * 防抖
 * @param fn
 * @param delay
 * @returns {function(...[*]=)}
 */
export function debounce(fn, delay = 500) {
  let timer;
  return () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      // eslint-disable-next-line prefer-rest-params
      fn.apply(this, arguments);
      timer = null;
    }, delay);
  };
}

/**
 * 节流
 * @param fn
 * @param delay
 * @returns {function(...[*]=)}
 */
export function throttle(fn, delay = 1000) {
  let last = 0;
  return function () {
    const now = new Date();
    if (now - last > delay) {
      // eslint-disable-next-line prefer-rest-params
      fn.apply(this, arguments);
      last = now;
    }
  };
}

/**
 * 通知客户端刷新页面
 * @param type 页面类型
 */
export function handleNoticeAppRefreshEvent(type) {
  const canRefresh = window.refreshEvent && typeof window.refreshEvent === 'function';
  if (canRefresh) {
    window.refreshEvent({ type });
  }
}

/**
 * 执行APP原生方法
 * @param fn
 * @param params
 */
export function handleRunAppFn(fn, params) {
  return new Promise((resolve, reject) => {
    const isFn = window[fn] && typeof window[fn] === 'function';
    const hasParams = typeof params !== 'undefined';
    if (!isAppWebView() || !isFn) return reject();
    if (hasParams) {
      window[fn](params);
    } else {
      window[fn]();
    }
    return resolve();
  });
}

/**
 * 安卓阻止字体大小调整
 */
export function handleFontSize() {
  const { WeixinJSBridge } = window;
  if (WeixinJSBridge && typeof WeixinJSBridge === 'object' && typeof WeixinJSBridge.invoke === 'function') {
    WeixinJSBridge.invoke('setFontSizeCallback', { fontSize: 0 });
    WeixinJSBridge.on('menu:setfont', () => {
      WeixinJSBridge.invoke('setFontSizeCallback', { fontSize: 0 });
    });
  }
}

export function limitFontSize() {
  const { WeixinJSBridge } = window;
  if (WeixinJSBridge && typeof WeixinJSBridge === 'object' && typeof WeixinJSBridge.invoke === 'function') {
    handleFontSize();
  } else if (document.addEventListener) {
    document.addEventListener('WeixinJSBridgeReady', handleFontSize, false);
  } else if (document.attachEvent) {
    document.attachEvent('WeixinJSBridgeReady', handleFontSize);
    document.attachEvent('onWeixinJSBridgeReady', handleFontSize);
  }
}
